import { z } from 'zod';
import type { EmbedTheme } from '@responsedock/types';

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
// rgb()/rgba()/hsl()/hsla() with only numbers, units, separators: no nested functions, no url().
const FUNCTIONAL_COLOR = /^(?:rgba?|hsla?)\(\s*[-+]?\d*\.?\d+(?:%|deg|rad|turn)?(?:\s*[,\s/]\s*[-+]?\d*\.?\d+%?){2,3}\s*\)$/i;

/** Hex, rgb()/rgba() or hsl()/hsla() colour (environment independent; used server side). */
export function isPortableCssColor(value: string): boolean {
  return HEX_COLOR.test(value) || FUNCTIONAL_COLOR.test(value);
}

// Characters a plain colour value never needs. Rejecting them up front keeps url(), var(),
// declarations and comments out even where CSS.supports would accept the syntax.
const UNSAFE_COLOR = /[^#a-zA-Z0-9(),.%\s/+-]|\b(?:url|var|env|attr|expression|image|element)\s*\(/i;

/**
 * True for any colour the current browser understands (named colours, oklch(), …), falling
 * back to hex/rgb/hsl when CSS.supports is unavailable (Node, older test DOMs).
 */
export function isSafeCssColor(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 64 || UNSAFE_COLOR.test(trimmed)) return false;
  const css = (globalThis as { CSS?: { supports?: (property: string, value: string) => boolean } }).CSS;
  if (typeof css?.supports === 'function') {
    try {
      return css.supports('color', trimmed);
    } catch {
      return isPortableCssColor(trimmed);
    }
  }
  return isPortableCssColor(trimmed);
}

const cssColorSchema = z.string().trim().refine(isPortableCssColor, 'Expected a hex, rgb() or hsl() CSS color.');
const lenientColorSchema = z.string().trim().refine(isSafeCssColor, 'Expected a CSS color.');

const webAssetUrlSchema = z.string().url().refine((value) => {
  return value.startsWith('https://') || /^http:\/\/localhost(?::\d+)?(?:\/|$)/.test(value);
}, 'Expected an HTTPS URL (HTTP is allowed only for localhost development).');

const cssRadiusSchema = z.union([
  z.number().min(0).max(9999),
  z.string().regex(/^\d+(?:\.\d+)?(?:px|rem|em|%)$/, 'Expected a non-negative CSS length.'),
]);

const fontFamilySchema = z.string().trim().max(120).refine(
  (value) => !/[;{}<>\\]|\/\*|\b(?:url|expression)\s*\(/i.test(value),
  'Expected a plain font-family list.',
);

const optional = <T extends z.ZodTypeAny>(schema: T) => schema.nullable().optional().transform((value) => value ?? undefined);

function themeShape(color: typeof cssColorSchema) {
  return {
    logoUrl: optional(webAssetUrlSchema),
    brandName: z.string().trim().max(100).nullable().optional().transform((value) => value || undefined),
    primaryColor: optional(color),
    primaryTextColor: optional(color),
    secondaryColor: optional(color),
    backgroundColor: optional(color),
    surfaceColor: optional(color),
    textColor: optional(color),
    mutedColor: optional(color),
    borderColor: optional(color),
    errorColor: optional(color),
    fontFamily: fontFamilySchema.nullable().optional().transform((value) => value || undefined),
    fontUrl: optional(webAssetUrlSchema),
    borderRadius: optional(cssRadiusSchema),
    buttonStyle: optional(z.enum(['rounded', 'square', 'pill'])),
    optionStyle: optional(z.enum(['bordered', 'minimal', 'pill'])),
    cardShadow: optional(z.enum(['none', 'subtle', 'elevated'])),
    questionSize: optional(z.enum(['small', 'medium', 'large'])),
    surveyWidth: optional(z.enum(['narrow', 'standard', 'wide'])),
    hidePlatformBranding: z.boolean().optional(),
  };
}

/** Shared runtime contract for branding supplied by an embedding host (strict: used by the API). */
export const embedThemeSchema = z.object(themeShape(cssColorSchema)).strict();

const lenientShape = themeShape(lenientColorSchema) as Record<string, z.ZodTypeAny>;

export function parseEmbedTheme(value: unknown): EmbedTheme {
  return embedThemeSchema.parse(value) as EmbedTheme;
}

export function mergeEmbedThemes(...themes: Array<EmbedTheme | null | undefined>): EmbedTheme {
  return parseEmbedTheme(Object.assign({}, ...themes.filter(Boolean)));
}

export interface LenientEmbedThemeResult {
  /** The valid subset. Keys explicitly set to null are present with `undefined` (meaning "clear"). */
  theme: EmbedTheme;
  /** Known fields whose value was invalid and was ignored. */
  rejected: string[];
  /** Unknown keys that were ignored. */
  ignored: string[];
}

/**
 * Field-by-field parse for themes received at runtime from a host page: applies every valid
 * field, ignores unknown keys and reports invalid ones instead of throwing. Never throws.
 */
export function parseEmbedThemeLenient(value: unknown): LenientEmbedThemeResult {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { theme: {}, rejected: value === undefined || value === null ? [] : ['theme'], ignored: [] };
  }
  const theme: Record<string, unknown> = {};
  const rejected: string[] = [];
  const ignored: string[] = [];
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const schema = Object.prototype.hasOwnProperty.call(lenientShape, key) ? lenientShape[key] : undefined;
    if (!schema) {
      ignored.push(key);
      continue;
    }
    const parsed = schema.safeParse(raw);
    if (parsed.success) theme[key] = parsed.data;
    else rejected.push(key);
  }
  return { theme: theme as EmbedTheme, rejected, ignored };
}

/** Lenient merge: later valid host fields win; `null` clears a field; invalid fields are skipped. */
export function mergeEmbedThemesLenient(
  current: EmbedTheme | null | undefined,
  incoming: unknown,
): LenientEmbedThemeResult {
  const result = parseEmbedThemeLenient(incoming);
  const merged: Record<string, unknown> = { ...(current ?? {}), ...result.theme };
  for (const key of Object.keys(merged)) if (merged[key] === undefined) delete merged[key];
  return { ...result, theme: merged as EmbedTheme };
}
